'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PricingTier {
  id: string;
  name: string;
  audience: string;
  monthlyPrice: number;
  annualPrice: number;
  description: string;
  prayerLimit: string;
  prayerNote: string;
  features: { text: string; included: boolean }[];
  highlight: boolean;
  badge?: string;
  ctaLabel: string;
  color: string;
  icon: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const tiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    audience: 'Just getting started',
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'Explore Rafeeq with basic tools to build your first habits and track daily prayers.',
    prayerLimit: '5 prayers/day tracked',
    prayerNote: 'Manual logging only',
    features: [
      { text: 'Prayer time reminders', included: true },
      { text: 'Basic habit tracker (3 habits)', included: true },
      { text: 'Sabr & Shukr journal', included: true },
      { text: 'Weekly streak tracking', included: true },
      { text: 'AI coaching (text)', included: false },
      { text: 'Voice AI coaching', included: false },
      { text: 'Academic Research Mode', included: false },
      { text: 'Scholar Q&A access', included: false },
      { text: 'Ambassador rewards', included: false },
    ],
    highlight: false,
    ctaLabel: 'Start Free',
    color: 'from-slate-500/10 to-slate-500/5',
    icon: '🌱',
  },
  {
    id: 'student',
    name: 'Student',
    audience: 'Secondary & high school students',
    monthlyPrice: 15,
    annualPrice: 12,
    description: 'Build daily discipline, manage studies, and grow spiritually with AI guidance tailored for student life.',
    prayerLimit: 'All 5 prayers tracked',
    prayerNote: 'Auto-detection + reminders',
    features: [
      { text: 'Prayer time reminders', included: true },
      { text: 'Unlimited habit tracker', included: true },
      { text: 'Sabr & Shukr journal', included: true },
      { text: 'Weekly progress report', included: true },
      { text: 'AI coaching (text)', included: true },
      { text: 'English + Arabic support', included: true },
      { text: 'Voice AI coaching', included: false },
      { text: 'Academic Research Mode', included: false },
      { text: 'Scholar Q&A access', included: false },
    ],
    highlight: false,
    ctaLabel: 'Start as Student',
    color: 'from-blue-500/10 to-blue-500/5',
    icon: '📖',
  },
  {
    id: 'undergraduate',
    name: 'Undergraduate',
    audience: 'University & college students',
    monthlyPrice: 20,
    annualPrice: 16,
    description: 'Navigate university pressures, career planning, and personal growth with advanced AI coaching.',
    prayerLimit: 'All 5 prayers + Tahajjud',
    prayerNote: 'Live azan + location-based times',
    features: [
      { text: 'Everything in Student', included: true },
      { text: 'Voice AI coaching (Advanced)', included: true },
      { text: 'Academic Research Mode', included: true },
      { text: 'Career Launchpad access', included: true },
      { text: 'Multi-language support (9 langs)', included: true },
      { text: 'Ambassador referral rewards', included: true },
      { text: 'Push notifications', included: true },
      { text: 'Scholar Q&A access', included: false },
      { text: 'Priority support', included: false },
    ],
    highlight: true,
    badge: 'Most Popular',
    ctaLabel: 'Start as Undergraduate',
    color: 'from-primary/10 to-primary/5',
    icon: '🎓',
  },
  {
    id: 'seniors',
    name: 'Seniors',
    audience: 'Muslim seniors & elders',
    monthlyPrice: 25,
    annualPrice: 20,
    description: 'Connect with verified Islamic scholars for personal answers — with the patience and depth seniors deserve.',
    prayerLimit: 'All 5 prayers + Sunnah',
    prayerNote: 'Azan + scholar prayer guidance',
    features: [
      { text: 'Everything in Undergraduate', included: true },
      { text: 'Scholar Directory access', included: true },
      { text: 'Personal scholar Q&A', included: true },
      { text: 'Verified sheikh responses', included: true },
      { text: 'Senior-friendly interface', included: true },
      { text: 'Priority support', included: true },
      { text: 'Milestone achievement sharing', included: true },
      { text: 'Islamic goal suggestions', included: true },
      { text: 'Dedicated onboarding', included: false },
    ],
    highlight: false,
    badge: 'For Seniors',
    ctaLabel: 'Start as Senior',
    color: 'from-emerald-500/10 to-emerald-500/5',
    icon: '🕌',
  },
  {
    id: 'premium',
    name: 'Premium',
    audience: 'Entrepreneurs & professionals',
    monthlyPrice: 30,
    annualPrice: 24,
    description: 'Full-spectrum AI ecosystem for business growth, spiritual leadership, and high-performance life management.',
    prayerLimit: 'All prayers + custom schedule',
    prayerNote: 'Business travel time-zone sync',
    features: [
      { text: 'Everything in Undergraduate', included: true },
      { text: 'Business strategy coaching', included: true },
      { text: 'Rafeeq Academic Research Assistant', included: true },
      { text: 'Priority voice response', included: true },
      { text: 'Custom AI persona tuning', included: true },
      { text: 'Dedicated onboarding call', included: true },
      { text: 'Scholar Q&A access', included: true },
      { text: 'White-glove support', included: true },
      { text: 'Early feature access', included: true },
    ],
    highlight: false,
    badge: 'Enterprise',
    ctaLabel: 'Start Premium',
    color: 'from-amber-500/10 to-amber-500/5',
    icon: '🚀',
  },
];

const faqs = [
  {
    q: 'Can I switch plans at any time?',
    a: 'Yes. Upgrade or downgrade anytime from your Settings → Subscription panel. Changes take effect at the next billing cycle.',
  },
  {
    q: 'What does "prayer limit" mean?',
    a: 'It refers to how many daily prayers Rafeeq actively tracks, reminds, and logs for you. Free users get manual logging for the 5 obligatory prayers; paid tiers add live azan, location-based times, and optional Sunnah/Tahajjud tracking.',
  },
  {
    q: 'Is the annual discount applied immediately?',
    a: 'Yes. When you choose annual billing, you pay the discounted rate upfront for 12 months — saving up to 20% compared to monthly.',
  },
  {
    q: 'Which payment methods are supported?',
    a: 'Card and mobile money payments for Western markets; M-Pesa for Kenya; MTN MoMo for Uganda; JazzCash/EasyPaisa for Pakistan; Midtrans for Indonesia; GrabPay/TNG for Malaysia; EVC Plus for Somalia.',
  },
  {
    q: 'Is my data private?',
    a: 'Absolutely. Rafeeq uses end-to-end encryption, zero-knowledge local storage for journals, and is fully GDPR compliant. Your spiritual reflections are never shared.',
  },
];

// ─── Components ───────────────────────────────────────────────────────────────

function PricingNav() {
  return (
    <header className="sticky top-0 z-50 bg-card/90 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link href="/home" className="flex items-center gap-2.5">
          <AppLogo size={30} />
          <span className="font-bold text-base text-foreground tracking-tight">Rafeeq</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-500 text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign in
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-600 px-4 py-2 rounded-xl gradient-teal text-white shadow-prayer hover:opacity-90 transition-opacity"
          >
            Get Started
          </Link>
        </div>
      </div>
    </header>
  );
}

function BillingToggle({
  annual,
  onToggle,
}: {
  annual: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-center gap-4">
      <span className={`text-sm font-500 ${!annual ? 'text-foreground' : 'text-muted-foreground'}`}>
        Monthly
      </span>
      <button
        onClick={onToggle}
        aria-label="Toggle billing period"
        className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
          annual ? 'bg-primary' : 'bg-muted-foreground/30'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 ${
            annual ? 'translate-x-7' : 'translate-x-0'
          }`}
        />
      </button>
      <span className={`text-sm font-500 flex items-center gap-2 ${annual ? 'text-foreground' : 'text-muted-foreground'}`}>
        Annual
        <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
          Save 20%
        </span>
      </span>
    </div>
  );
}

function TierCard({ tier, annual }: { tier: PricingTier; annual: boolean }) {
  const price = annual ? tier.annualPrice : tier.monthlyPrice;
  const isFree = tier.id === 'free';

  const ctaHref = isFree
    ? '/sign-up-login-screen'
    : `/checkout?tier=${tier.id}&annual=${annual}`;

  return (
    <div
      className={`relative flex flex-col rounded-2xl border transition-all duration-300 hover:-translate-y-1 overflow-hidden ${
        tier.highlight
          ? 'border-primary shadow-prayer bg-gradient-to-b from-primary/5 to-card'
          : 'border-border bg-card shadow-card hover:shadow-card-hover'
      }`}
    >
      {/* Top accent bar */}
      <div className={`h-1 w-full bg-gradient-to-r ${tier.color.replace('from-', 'from-').replace('/10', '').replace('/5', '')}`} />

      {tier.badge && (
        <div
          className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-700 tracking-wide ${
            tier.highlight
              ? 'gradient-teal text-white shadow-prayer'
              : 'bg-accent/10 text-accent border border-accent/20'
          }`}
        >
          {tier.badge}
        </div>
      )}

      <div className="p-7 flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-5">
          <div className="text-2xl mb-2">{tier.icon}</div>
          <h3 className="text-lg font-700 text-foreground">{tier.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{tier.audience}</p>
        </div>

        {/* Price */}
        <div className="mb-1">
          {isFree ? (
            <div className="flex items-end gap-1">
              <span className="text-4xl font-800 text-foreground">Free</span>
            </div>
          ) : (
            <div className="flex items-end gap-1">
              <span className="text-4xl font-800 text-foreground">${price}</span>
              <span className="text-muted-foreground text-sm mb-1.5">/mo</span>
            </div>
          )}
          {!isFree && annual && (
            <p className="text-xs text-emerald-600 font-500 mt-0.5">
              Billed ${price * 12}/year · Save ${(tier.monthlyPrice - price) * 12}/yr
            </p>
          )}
          {!isFree && !annual && (
            <p className="text-xs text-muted-foreground mt-0.5">Billed monthly · cancel anytime</p>
          )}
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-5">{tier.description}</p>

        {/* Prayer limit badge */}
        <div className="flex items-start gap-2.5 bg-muted/60 border border-border rounded-xl px-3.5 py-3 mb-5">
          <span className="text-base mt-0.5">🕋</span>
          <div>
            <p className="text-xs font-700 text-foreground">{tier.prayerLimit}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{tier.prayerNote}</p>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2.5 flex-1">
          {tier.features.map((f) => (
            <li key={f.text} className={`flex items-start gap-2.5 text-sm ${f.included ? 'text-foreground' : 'text-muted-foreground/50'}`}>
              {f.included ? (
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {f.text}
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <div className="px-7 pb-7">
        <Link
          href={ctaHref}
          className={`block w-full text-center py-3 rounded-xl font-600 text-sm transition-all duration-200 hover:scale-[1.02] ${
            tier.highlight
              ? 'gradient-teal text-white shadow-prayer hover:opacity-90'
              : isFree
              ? 'bg-muted text-foreground border border-border hover:bg-secondary'
              : 'bg-muted text-foreground border border-border hover:bg-secondary'
          }`}
        >
          {tier.ctaLabel}
        </Link>
        {!isFree && (
          <p className="text-center text-xs text-muted-foreground mt-2">
            Pay in your local currency ↓
          </p>
        )}
      </div>
    </div>
  );
}

function ComparisonTable({ annual }: { annual: boolean }) {
  const allFeatures = [
    'Prayer time reminders',
    'Habit tracker',
    'Sabr & Shukr journal',
    'Weekly streak tracking',
    'AI coaching (text)',
    'Voice AI coaching',
    'Multi-language support',
    'Academic Research Mode',
    'Career Launchpad',
    'Ambassador rewards',
    'Push notifications',
    'Scholar Q&A access',
    'Priority support',
    'Custom AI persona',
  ];

  const tierIncludes: Record<string, string[]> = {
    free: ['Prayer time reminders', 'Habit tracker', 'Sabr & Shukr journal', 'Weekly streak tracking'],
    student: ['Prayer time reminders', 'Habit tracker', 'Sabr & Shukr journal', 'Weekly streak tracking', 'AI coaching (text)', 'Multi-language support'],
    undergraduate: ['Prayer time reminders', 'Habit tracker', 'Sabr & Shukr journal', 'Weekly streak tracking', 'AI coaching (text)', 'Voice AI coaching', 'Multi-language support', 'Academic Research Mode', 'Career Launchpad', 'Ambassador rewards', 'Push notifications'],
    seniors: ['Prayer time reminders', 'Habit tracker', 'Sabr & Shukr journal', 'Weekly streak tracking', 'AI coaching (text)', 'Voice AI coaching', 'Multi-language support', 'Academic Research Mode', 'Career Launchpad', 'Ambassador rewards', 'Push notifications', 'Scholar Q&A access', 'Priority support'],
    premium: allFeatures,
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-4 pr-6 font-600 text-muted-foreground w-48">Feature</th>
            {tiers.map((t) => (
              <th key={t.id} className="text-center py-4 px-3 font-700 text-foreground min-w-[100px]">
                <div className="flex flex-col items-center gap-1">
                  <span>{t.icon}</span>
                  <span>{t.name}</span>
                  <span className="text-xs font-500 text-muted-foreground">
                    {t.id === 'free' ? 'Free' : `$${annual ? t.annualPrice : t.monthlyPrice}/mo`}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allFeatures.map((feature, i) => (
            <tr key={feature} className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-muted/20' : ''}`}>
              <td className="py-3 pr-6 text-foreground font-500">{feature}</td>
              {tiers.map((t) => (
                <td key={t.id} className="py-3 px-3 text-center">
                  {tierIncludes[t.id].includes(feature) ? (
                    <svg className="w-4 h-4 mx-auto text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <span className="text-muted-foreground/30 text-base">—</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FaqSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="py-20 px-6 bg-muted/30">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-700 text-primary uppercase tracking-widest mb-2">FAQ</p>
          <h2 className="text-3xl font-800 text-foreground">Common questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-card border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/40 transition-colors"
              >
                <span className="font-600 text-foreground text-sm">{faq.q}</span>
                <svg
                  className={`w-4 h-4 text-muted-foreground flex-shrink-0 ml-4 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
              {open === i && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [showTable, setShowTable] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <PricingNav />

      {/* Hero */}
      <section className="pt-16 pb-10 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-accent/5 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-600 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Simple, transparent pricing
          </div>
          <h1 className="text-4xl font-800 text-foreground mb-4 leading-tight">
            Choose your path to<br />
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              purposeful growth
            </span>
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed mb-8">
            Every tier includes prayer tracking, habit tools, and Islamic guidance. Upgrade for AI coaching, voice features, and scholar access.
          </p>
          <BillingToggle annual={annual} onToggle={() => setAnnual(!annual)} />
        </div>
      </section>

      {/* Tier Cards */}
      <section className="px-6 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {tiers.map((tier) => (
            <TierCard key={tier.id} tier={tier} annual={annual} />
          ))}
        </div>
      </section>

      {/* Regional payment note */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="text-3xl">🌍</div>
          <div className="flex-1">
            <h3 className="font-700 text-foreground mb-1">Pay in your local currency</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Prices above are USD references. After signing up, choose your regional payment page — M-Pesa (Kenya), MTN MoMo (Uganda), JazzCash/EasyPaisa (Pakistan), Midtrans (Indonesia), GrabPay/TNG (Malaysia), EVC Plus (Somalia), or card for Western markets.
            </p>
          </div>
          <Link
            href="/home#pricing"
            className="flex-shrink-0 text-sm font-600 px-4 py-2.5 rounded-xl bg-muted border border-border text-foreground hover:bg-secondary transition-colors"
          >
            View regions →
          </Link>
        </div>
      </section>

      {/* Feature comparison toggle */}
      <section className="px-6 pb-6">
        <div className="max-w-7xl mx-auto text-center">
          <button
            onClick={() => setShowTable(!showTable)}
            className="inline-flex items-center gap-2 text-sm font-600 text-primary hover:text-primary/80 transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showTable ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
            {showTable ? 'Hide' : 'Show'} full feature comparison
          </button>
        </div>
      </section>

      {/* Comparison table */}
      {showTable && (
        <section className="px-6 pb-16">
          <div className="max-w-7xl mx-auto bg-card border border-border rounded-2xl p-6 overflow-hidden">
            <h2 className="text-lg font-700 text-foreground mb-6">Full feature comparison</h2>
            <ComparisonTable annual={annual} />
          </div>
        </section>
      )}

      {/* Prayer limits detail */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-700 text-primary uppercase tracking-widest mb-2">Prayer Tracking</p>
            <h2 className="text-2xl font-800 text-foreground">What each tier tracks</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers.filter((t) => t.id !== 'free').map((tier) => (
              <div key={tier.id} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="text-xl">{tier.icon}</span>
                  <span className="font-700 text-foreground text-sm">{tier.name}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🕋</span>
                    <span className="text-sm font-600 text-foreground">{tier.prayerLimit}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">{tier.prayerNote}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqSection />

      {/* Bottom CTA */}
      <section className="py-20 px-6 text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl font-800 text-foreground mb-4">
            Begin your journey today
          </h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Join thousands of Muslims building consistent, purposeful lives with Rafeeq. Start free, upgrade when you&apos;re ready.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/sign-up-login-screen"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl gradient-teal text-white font-600 text-base shadow-prayer hover:opacity-90 transition-all hover:scale-[1.02]"
            >
              Get Started Free
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
            <Link
              href="/home"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-card border border-border text-foreground font-500 text-base hover:bg-muted transition-colors"
            >
              Learn more
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-600 text-foreground">Rafeeq</span>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 Rafeeq. All rights reserved.</p>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <Link href="/home" className="hover:text-foreground transition-colors">Home</Link>
            <Link href="/sign-up-login-screen" className="hover:text-foreground transition-colors">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
